import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";
import { isContactHref } from "../lib/cta";

const BASE = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   /showcase — Sunday Digital · Selected work
   11 curated case studies, each pinned across ~2 viewports.
   While a project is pinned, the right-side imagery scrolls
   vertically through TWO frames while the left copy column
   evolves. Same editorial DNA as About / Creative.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);


/* ============================================================
   01 · Hero
============================================================ */
const ShowcaseHero = ({ onPrimary, count = 11, marqueeProjects = [] }) => {
  const cms = useContent("showcase");
  const h = cms?.hero || {};
  const m = cms?.marquee || {};
  const breadcrumb = h.breadcrumb || "Showcase";
  const eyebrowTpl = h.eyebrow || "Selected work · {count} {stories}";
  const eyebrow = eyebrowTpl
    .replace(/\{count\}/g, String(count))
    .replace(/\{stories\}/g, count === 1 ? "story" : "stories");
  const titleLead = h.title_lead || "Show";
  const titleItalic = h.title_italic || "case";
  const subtitle = h.subtitle ||
    "We love what we do and the clients we work with. Each project handled with efficiency — exceptional results every time, and long-term partnerships built along the way.";
  const ctaPrimaryLabel = h.cta_primary_label || "Start a project";
  const ctaSecondaryLabel = h.cta_secondary_label || "See the work";
  const ctaSecondaryHref = h.cta_secondary_href || "#projects";

  // Marquee — `use_auto` = take case-study names automatically.
  // When OFF, render the manually-curated `items` list instead.
  const useAuto = m.use_auto === undefined ? true : !!m.use_auto;
  const marqueeNames = useAuto
    ? (marqueeProjects || []).map((p) => p.name).filter(Boolean)
    : (Array.isArray(m.items) ? m.items.map((it) => it.title || "").filter(Boolean) : []);

  return (
  <section
    id="top"
    data-testid="showcase-hero"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    {/* Soft yellow ambient flare */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(900px 600px at 80% 30%, rgba(244,214,78,0.10), transparent 60%)",
      }}
    />
    {/* Grain */}
    <span
      aria-hidden
      className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
        backgroundSize: "3px 3px",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24 md:pb-32">
      {/* Breadcrumb */}
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-12 md:mb-16"
      >
        <Link to="/" className="hover:text-[#F4D64E] transition-colors">Sunday</Link>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">{breadcrumb}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-14 items-end">
        <div className="lg:col-span-8">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h1
            data-testid="showcase-hero-title"
            className="font-display mt-8 text-white leading-[0.9] tracking-[-0.045em] font-black text-[3.75rem] sm:text-[5.5rem] md:text-[8rem] lg:text-[10rem] xl:text-[12rem]"
          >
            <span data-reveal="mask-block">
              <span>
                {titleLead}
                <span className="italic font-normal text-neutral-400">{titleItalic}</span>
                <span className="text-[#F4D64E]">.</span>
              </span>
            </span>
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="max-w-[42ch] text-[1.05rem] md:text-[1.18rem] text-neutral-300 leading-[1.6]"
          >
            <span>{subtitle}</span>
          </p>
          <div
            data-reveal="eyebrow"
            style={{ "--i": 2 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <PrimaryCTA testId="showcase-hero-cta" onClick={onPrimary}>
              {ctaPrimaryLabel}
            </PrimaryCTA>
            {isContactHref(ctaSecondaryHref) ? (
              <button
                type="button"
                onClick={onPrimary}
                className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-300 hover:text-[#F4D64E] transition-colors"
              >
                {ctaSecondaryLabel}
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </button>
            ) : (
              <a
                href={ctaSecondaryHref}
                className="group inline-flex items-center gap-2 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-300 hover:text-[#F4D64E] transition-colors"
              >
                {ctaSecondaryLabel}
                <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Bottom marquee — running brand index */}
    {marqueeNames.length > 0 ? (
    <div className="relative border-t border-white/[0.08] bg-[#0B0F14]/65 backdrop-blur-md">
      <div className="overflow-hidden">
        <motion.div
          className="flex items-center gap-12 py-4 whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 60, ease: "linear", repeat: Infinity }}
        >
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-12 shrink-0">
              {marqueeNames.concat(marqueeNames).map((name, i) => (
                <span
                  key={`${dup}-${i}-${name}`}
                  className="flex items-center gap-12"
                >
                  <span className="font-display text-[1.3rem] md:text-[1.6rem] font-black tracking-[-0.015em] text-white uppercase">
                    {name}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]" />
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
    ) : null}
  </section>
  );
};

/* MediaTile — a full-viewport-height media frame on the right
   side of a project. Auto-plays its video when in view, pauses
   when scrolled past. Uses IntersectionObserver so only the
   tile actually on screen consumes bandwidth/decode budget. */
const MediaTile = ({ frame, alt, frameNum, projectNumeral, showNumeral }) => {
  const ref = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const v = videoRef.current;
    if (!el) return;
    if (frame.type !== "video" || !v) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (v.readyState < 2) {
            try {
              v.load();
            } catch (_e) {
              /* noop */
            }
          }
          const promise = v.play();
          if (promise && typeof promise.catch === "function") {
            promise.catch(() => {});
          }
        } else {
          v.pause();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [frame]);

  return (
    <div
      ref={ref}
      data-testid={`media-tile-${frameNum}`}
      className="relative h-screen overflow-hidden bg-[#0F141A]"
    >
      {frame.type === "video" ? (
        <video
          ref={videoRef}
          src={frame.src}
          poster={frame.poster}
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={frame.src}
          alt={alt}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Subtle gradient softening the left edge so copy remains legible */}
      <span
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/40 via-transparent to-transparent lg:from-[#0B0F14]/55 pointer-events-none"
      />

      {/* Frame badge — top right */}
      <div className="absolute top-6 right-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-black/40 backdrop-blur-md border border-white/10 text-[0.6rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
        {frame.type === "video" ? (
          <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E] animate-pulse" />
        ) : null}
        Frame {frameNum} / 02
      </div>

      {/* Giant project numeral — only on the FIRST tile to avoid repetition */}
      {showNumeral && (
        <span
          aria-hidden
          className="absolute -bottom-12 -right-6 lg:-right-10 font-display font-black text-[#F4D64E]/[0.16] leading-[0.78] tracking-[-0.05em] pointer-events-none select-none"
          style={{ fontSize: "clamp(10rem, 22vw, 24rem)" }}
        >
          {projectNumeral}
        </span>
      )}
    </div>
  );
};

/* ============================================================
   ProjectStation — left copy is `position: sticky` inside its
   own grid cell. Right column has two stacked full-screen
   media tiles. As the user scrolls, the right side reveals
   media 1, then media 2; the left stays pinned through both.
   When media 2 has scrolled past, the left's sticky boundary
   ends naturally and scroll continues into the next project.
============================================================ */
const ProjectStation = ({ project, total }) => (
  <section
    data-testid={`project-${project.n}`}
    className="relative bg-[#0B0F14] border-b border-white/[0.04]"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12">
      {/* LEFT — copy column, pins inside section */}
      <div className="lg:col-span-5 lg:sticky lg:top-0 lg:h-screen flex items-center px-6 md:px-12 lg:pl-20 lg:pr-10 py-16 md:py-20 z-20 bg-gradient-to-r from-[#0B0F14] via-[#0B0F14]/95 to-transparent lg:bg-none">
        <div className="relative max-w-xl">
          {/* Top index chip */}
          <div className="flex items-center gap-4 mb-7 text-[0.65rem] md:text-[0.7rem] font-bold tracking-[0.3em] uppercase">
            <span className="text-[#F4D64E] tabular-nums">
              {project.n} / {String(total).padStart(2, "0")}
            </span>
            <span className="h-px flex-1 bg-white/[0.12] max-w-[80px]" />
            <span className="text-neutral-500">{project.industry}</span>
          </div>

          <h2 className="font-display text-[2.4rem] sm:text-[3.25rem] md:text-[3.75rem] lg:text-[4.25rem] xl:text-[5rem] font-black tracking-[-0.04em] leading-[0.96] text-white">
            <span className="block">{project.name.split(" ")[0]}</span>
            {project.name.split(" ").slice(1).length > 0 && (
              <span className="block italic font-normal text-neutral-400">
                {project.name.split(" ").slice(1).join(" ")}
              </span>
            )}
          </h2>

          <p className="mt-7 text-[0.95rem] md:text-[1.05rem] text-neutral-300 leading-[1.6] max-w-[44ch]">
            {project.description}
          </p>

          {/* Services */}
          <ul className="mt-7 grid grid-cols-1 gap-y-2.5 max-w-[42ch]">
            {project.services.map((s) => (
              <li
                key={s}
                className="flex items-baseline gap-3 text-[0.85rem] md:text-[0.92rem] text-neutral-400 leading-[1.4]"
              >
                <span className="text-[#F4D64E] font-bold tracking-[0.3em] tabular-nums text-[0.65rem] uppercase mt-0.5 shrink-0 w-6">
                  —
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ul>

          {/* Footer */}
          <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="inline-flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
              <span className="tabular-nums">{project.year}</span>
              <span className="w-px h-3 bg-white/[0.15]" />
              <span className="text-[#F4D64E]">In partnership</span>
            </div>
            {project.caseStudyHref && (
              <Link
                to={project.caseStudyHref}
                data-testid={`case-study-link-${project.n}`}
                className="group inline-flex items-center gap-2 text-[0.72rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
              >
                Read case study
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT — stacked media tiles, scroll naturally */}
      <div className="lg:col-span-7 order-first lg:order-last">
        <MediaTile
          frame={project.frames[0]}
          alt={`${project.name} frame 1`}
          frameNum="01"
          projectNumeral={project.n}
          showNumeral={true}
        />
        <MediaTile
          frame={project.frames[1]}
          alt={`${project.name} frame 2`}
          frameNum="02"
          projectNumeral={project.n}
          showNumeral={false}
        />
      </div>
    </div>
  </section>
);

/* ============================================================
   03 · Closing manifesto / CTA
============================================================ */
const Closing = ({ onContact, count = 11 }) => {
  const cms = useContent("showcase");
  const c = cms?.closing || {};
  const ordinal = (n) => {
    const words = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve","thirteen","fourteen","fifteen","sixteen","seventeen","eighteen","nineteen","twenty"];
    return words[n] || String(n);
  };
  const nextOrdinal = ordinal(count + 1);
  const countOrdinal = ordinal(count);
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const fillTokens = (s) => (s || "")
    .replace(/\{count\}/g, String(count))
    .replace(/\{count_word\}/g, countOrdinal)
    .replace(/\{count_word_cap\}/g, cap(countOrdinal))
    .replace(/\{next_word\}/g, nextOrdinal)
    .replace(/\{next_word_cap\}/g, cap(nextOrdinal));

  const eyebrow = c.eyebrow || "The work continues";
  const headlineLead = fillTokens(c.headline_lead || "{count_word_cap} stories.");
  const headlineItalic = fillTokens(c.headline_italic || "Yours could be");
  const headlineAccent = fillTokens(c.headline_accent || "{next_word}");
  const ctaPrimaryLabel = c.cta_primary_label || "Start a project";
  const ctaSecondaryLabel = c.cta_secondary_label || "Read the studio story";
  const ctaSecondaryHref = c.cta_secondary_href || "/about";

  return (
  <section
    data-testid="showcase-closing"
    className="relative bg-[#0B0F14] border-t border-white/[0.06] overflow-hidden"
  >
    <span
      aria-hidden
      className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#F4D64E]/[0.08] blur-3xl"
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
      <div className="max-w-4xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-8 font-display text-[2.25rem] sm:text-[3rem] md:text-[4rem] lg:text-[5rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
        >
          <span>
            {headlineLead}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
              </>
            ) : null}
            {headlineAccent ? <PillarAccent>{headlineAccent}</PillarAccent> : null}
            .
          </span>
        </h2>
        <div
          data-reveal="eyebrow"
          style={{ "--i": 2 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <PrimaryCTA testId="showcase-closing-cta" onClick={onContact}>
            {ctaPrimaryLabel}
          </PrimaryCTA>
          {isContactHref(ctaSecondaryHref) ? (
            <button
              type="button"
              onClick={onContact}
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] hover:text-white transition-colors"
            >
              {ctaSecondaryLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          ) : (
            <Link
              to={ctaSecondaryHref}
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] hover:text-white transition-colors"
            >
              {ctaSecondaryLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
// Normalize an API case-study doc into the rich shape the index page
// renders. Newly-duplicated/empty case studies fall back to the hero
// image for both frames so nothing crashes if the admin hasn't filled
// in `frames` / `services` / `description` yet.
const normalizeProject = (p) => {
  const fallbackHero = p.heroImg || "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1800&q=75";
  const frames = (Array.isArray(p.frames) && p.frames.length >= 2 ? p.frames.slice(0, 2) : null) || [
    { type: "image", src: fallbackHero },
    { type: "image", src: fallbackHero },
  ];
  // Default to true so existing case studies (without the field) keep showing the link.
  const showLink = p.showCardLink === false ? false : true;
  return {
    n: p.n || "—",
    name: p.name || "Untitled case study",
    industry: p.industry || "",
    year: p.year || "",
    caseStudyHref: showLink ? `/showcase/${p.slug}` : "",
    description: p.description || p.tagline || "",
    services: Array.isArray(p.services) && p.services.length ? p.services : [],
    frames,
  };
};

export default function ShowcasePage() {
  const { open: openContact } = useContactPanel();
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState("");

  useSeo({
    route: "/showcase",
    fallback: {
      title: "Showcase — Sunday Digital",
      description: "Selected work — long-form case studies.",
    },
  });
  useEffect(() => {
    document.title = "Showcase — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Fetch case studies from the CMS
  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE}/api/showcase?public=true&_=${Date.now()}`, {
      credentials: "omit",
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((rows) => {
        if (cancelled) return;
        const normalized = (rows || []).map(normalizeProject);
        // Re-number sequentially so newly-created items sort cleanly.
        normalized.forEach((p, i) => {
          p.n = String(i + 1).padStart(2, "0");
        });
        setProjects(normalized);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load case studies.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reveal observer — same as the rest of the site
  useEffect(() => {
    if (projects === null) return;
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [projects]);

  // Preload all image frames so transitions never dark-flash.
  useEffect(() => {
    if (!projects) return;
    projects.forEach((p) => {
      p.frames.forEach((f) => {
        if (f.type === "image") {
          const im = new window.Image();
          im.src = f.src;
        } else if (f.poster) {
          const im = new window.Image();
          im.src = f.poster;
        }
      });
    });
  }, [projects]);

  return (
    <div
      data-testid="showcase-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <ShowcaseHero
          onPrimary={openContact}
          count={projects ? projects.length : 0}
          marqueeProjects={projects || []}
        />
        <div id="projects">
          {projects === null && !error ? (
            <div
              data-testid="showcase-loading"
              className="h-screen flex items-center justify-center text-[0.7rem] tracking-[0.3em] uppercase text-neutral-500"
            >
              Loading work…
            </div>
          ) : error ? (
            <div
              data-testid="showcase-error"
              className="h-[60vh] flex items-center justify-center text-sm text-neutral-400"
            >
              {error}
            </div>
          ) : projects.length === 0 ? (
            <div className="h-[60vh] flex items-center justify-center text-sm text-neutral-500">
              No case studies yet.
            </div>
          ) : (
            projects.map((p) => (
              <ProjectStation
                key={p.n + p.name}
                project={p}
                total={projects.length}
              />
            ))
          )}
        </div>
        <Closing onContact={openContact} count={projects ? projects.length : 11} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
